import { select, selectAll } from "@fruugo/utilities";
import {
  closeSideMenu,
  openSideMenu,
  sideMenuClassConfig,
  SideMenuSettings,
} from "./SideMenu";
import { i18n } from "../../i18n";
import { Icon } from "../Icon";
import { GAListInfo, Sku } from "../../../types";
import { getItemsFromOtherSellers } from "../../api";
import { Loader } from "../Loader";
import { Alert } from "../Alert";
import {
  createGAItem,
  sendViewItemListEvent,
} from "../../analytics/google-analytics";
import { fadeInItems, SellersItemList } from "./SellersItemList";
import { SelectSellerComparison } from "./SelectSellerComparison";
import { getListInfo } from "../../analytics";

let listItems: Sku[] | null = null;

const GA_LIST_ID = "select_seller_menu";
const GA_LIST_NAME = "Select Seller Menu";

type ComparisonInfo = {
  title: string;
  imageUrl: string;
};

// Select Seller Menu appears on the listing pages when a sku is sold by multiple retailers
export function initSelectSellerMenu() {
  const config = {
    openClass: ".js-open-select-seller-menu",
  };

  const sideMenuSettings: SideMenuSettings = {
    position: "right",
    disableScroll: true,
  };

  function openMenu(
    productId: string,
    listInfo: GAListInfo | null,
    comparisonInfo?: ComparisonInfo
  ) {
    openSideMenu(<SelectSellerMenu />, sideMenuSettings);
    getItemsFromOtherSellers(productId)
      .then((items) => {
        listItems = items;
        sendViewItemListEvent(
          GA_LIST_ID,
          listInfo?.listName ?? GA_LIST_NAME,
          items.map((item) => createGAItem(item))
        );
      })
      .catch(console.error)
      .finally(() => render(listItems, comparisonInfo))
      .then(fadeInItems);
  }

  selectAll<HTMLElement>(config.openClass)?.forEach((item) => {
    const { productId } = item.dataset;
    if (productId) {
      item.addEventListener("click", (e) => {
        const target = e.currentTarget as HTMLElement;
        const parent = target.closest("[data-item-parent]");
        const title = parent?.querySelector("[data-item-title]")?.textContent;
        const imageUrl = (
          parent?.querySelector("[data-item-image]") as HTMLImageElement | null
        )?.src;

        const comparisonInfo =
          title && imageUrl
            ? {
                title,
                imageUrl,
              }
            : undefined;

        const listInfo = item ? getListInfo(item) : null;

        openMenu(productId, listInfo, comparisonInfo);
      });
    }
  });
}

function render(
  listItems: Sku[] | null,
  comparisonInfo: ComparisonInfo | undefined
) {
  const root = select(".js-select-seller-root");
  const loading = select(".js-select-seller-loading");
  if (!root) return;

  if (loading) loading.remove();

  if (!listItems || listItems.length == 0) {
    // Show error alert if fetch errors or there's no items returned
    root.replaceWith(
      <div class="tw:p-4">
        <Alert text={i18n("product.multi-merchant.error")} />
      </div>
    );
  } else {
    root.replaceWith(
      <>
        {comparisonInfo && (
          <SelectSellerComparison
            productTitle={comparisonInfo.title}
            imageUrl={comparisonInfo.imageUrl}
          />
        )}
        <SellersItemList
          listItems={listItems}
          gaListId={GA_LIST_ID}
          gaListName={GA_LIST_NAME}
        />
      </>
    );
  }
}

export const SelectSellerMenu = () => {
  return (
    <div class="tw:flex tw:flex-col tw:h-full">
      <header class="d-flex justify-content-between align-items-center sticky-section">
        <h3 class="tw:m-0 tw:font-semibold tw:text-xl tw:text-fruugo-black">
          {i18n("product.select-seller.menu.title")}
        </h3>
        <button
          type="button"
          title={i18n("search.page.close")}
          onClick={closeSideMenu}
          class={`button-icon header-icon clear ${sideMenuClassConfig.closeSideMenuClass.replace(
            ".",
            ""
          )}`}
        >
          <Icon name="close" width={24} height={24} />
        </button>
      </header>
      <div class="js-select-seller-loading tw:flex tw:flex-1 tw:items-center tw:justify-center">
        <Loader />
      </div>
      <div class="js-select-seller-root"></div>
    </div>
  );
};
